import React, { useState, useRef, useEffect, useCallback } from 'react';
import { RotateCcw, Maximize2, Minimize2 } from 'lucide-react';
import { quickTemplates } from './geogebraTemplates';

/**
 * Composant GeoGebra avec éditeur complet et templates rapides
 */
const GeogebraElement = ({ config, onConfigChange, onRemove, isPreview = false }) => {
  const [ggbReady, setGgbReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const ggbApiRef = useRef(null);
  const ggbAppletRef = useRef(null);
  const ggbElementId = useRef(`ggb-${Date.now()}`);
  const initializedRef = useRef(false);
  const initialConfigRef = useRef(config?.ggb_base64);
  const onConfigChangeRef = useRef(onConfigChange);
  const saveTimeoutRef = useRef(null);
  const intervalIdsRef = useRef([]);
  const ggbZoneRef = useRef(null);

  // Sync ref à chaque render (pas de useEffect nécessaire pour une ref)
  onConfigChangeRef.current = onConfigChange;

  // Charger GeoGebra depuis le CDN
  const loadGeoGebra = useCallback(async () => {
    if (typeof window.GGBApplet !== 'undefined') {
      return true;
    }

    const urls = [
      'https://www.geogebra.org/apps/deployggb.js',
      'https://cdn.geogebra.org/apps/deployggb.js'
    ];

    for (const url of urls) {
      try {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = url;
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
        if (typeof window.GGBApplet !== 'undefined') {
          return true;
        }
      } catch (e) {
        console.warn('Failed to load GeoGebra from', url);
      }
    }
    return false;
  }, []);

  // Sauvegarder avec debounce
  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      if (ggbApiRef.current) {
        const api = ggbApiRef.current;
        const base = {
          ggb_base64: api.getBase64(),
          ggb_image: api.getPNGBase64(1, true, 72)
        };

        // Export SVG pour l'aperçu élève
        try {
          if (typeof api.exportSVG === 'function') {
            api.exportSVG((svgStr) => {
              onConfigChangeRef.current({ ...base, ggb_svg: svgStr || null });
            });
          } else {
            onConfigChangeRef.current(base);
          }
        } catch (e) {
          onConfigChangeRef.current(base);
        }
      }
    }, 500);
  }, []);

  // Appliquer un template
  const applyTemplate = useCallback((template) => {
    if (!ggbReady || !ggbApiRef.current) return;

    const api = ggbApiRef.current;
    api.reset();
    template.generate(api);
    api.evalCommand('ZoomIn(-6, -5, 8, 6)');

    // Sauvegarder après application du template
    setTimeout(() => debouncedSave(), 100);
  }, [ggbReady, debouncedSave]);

  // Réinitialiser le canvas
  const resetCanvas = useCallback(() => {
    if (!ggbReady || !ggbApiRef.current) return;
    ggbApiRef.current.reset();
    debouncedSave();
  }, [ggbReady, debouncedSave]);

  // Toggle plein écran (juste le state, le resize est dans le useEffect)
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Redimensionner GeoGebra + écouter Escape quand fullscreen change
  useEffect(() => {
    if (!ggbApiRef.current) return;

    // Attendre le prochain frame pour que le DOM ait ses nouvelles dimensions
    const rafId = requestAnimationFrame(() => {
      if (!ggbApiRef.current) return;
      if (isFullscreen) {
        // Mesurer le conteneur réel pour dimensionner GeoGebra précisément
        if (ggbZoneRef.current) {
          const rect = ggbZoneRef.current.getBoundingClientRect();
          ggbApiRef.current.setWidth(Math.floor(rect.width) - 2);
          ggbApiRef.current.setHeight(Math.floor(rect.height) - 2);
        }
      } else if (initializedRef.current) {
        ggbApiRef.current.setWidth(700);
        ggbApiRef.current.setHeight(400);
      }
    });

    if (!isFullscreen) return () => cancelAnimationFrame(rafId);

    const handleKey = (e) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', handleKey);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('keydown', handleKey);
    };
  }, [isFullscreen]);

  // Fonction de nettoyage complète de GeoGebra
  const cleanupGeoGebra = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    if (ggbApiRef.current) {
      try {
        ggbApiRef.current.unregisterAddListener();
        ggbApiRef.current.unregisterUpdateListener();
        ggbApiRef.current.unregisterRemoveListener();
      } catch (e) {}
      ggbApiRef.current = null;
    }

    if (ggbAppletRef.current) {
      try {
        if (typeof ggbAppletRef.current.remove === 'function') {
          ggbAppletRef.current.remove();
        }
      } catch (e) {}
      ggbAppletRef.current = null;
    }

    const containerId = ggbElementId.current;
    const container = document.getElementById(containerId);
    if (container) {
      const iframe = container.querySelector('iframe');
      if (iframe) {
        iframe.src = 'about:blank';
        iframe.remove();
      }
      container.innerHTML = '';
    }

    if (window[containerId]) {
      try {
        delete window[containerId];
      } catch (e) {
        window[containerId] = undefined;
      }
    }

    if (intervalIdsRef.current.length > 0) {
      intervalIdsRef.current.forEach(id => clearInterval(id));
      intervalIdsRef.current = [];
    }

    initializedRef.current = false;
    setGgbReady(false);
  }, []);

  // Initialiser GeoGebra
  useEffect(() => {
    if (isPreview && !initialConfigRef.current) return;
    if (initializedRef.current) return;
    initializedRef.current = true;

    const init = async () => {
      const loaded = await loadGeoGebra();
      if (!loaded) {
        setLoading(false);
        return;
      }

      const ggbParams = {
        appName: 'classic',
        perspective: 'G',
        width: isPreview ? 500 : 700,
        height: isPreview ? 350 : 400,
        showToolBar: !isPreview,
        showAlgebraInput: false,
        showAlgebraView: false,
        algebraWidth: 0,
        showMenuBar: false,
        showResetIcon: false,
        showToolBarHelp: !isPreview,
        enableLabelDrags: !isPreview,
        enableShiftDragZoom: true,
        enableRightClick: !isPreview,
        enableUndoRedo: !isPreview,
        enableCAS: false,
        lockToGrid: false,
        disableAutoScale: isPreview,
        showZoomButtons: isPreview,
        showFullscreenButton: false,
        showSuggestionButtons: false,
        showAuxiliaryObjects: false,
        allowStyleBar: !isPreview,
        showAnimationButton: false,
        showStartTooltip: false,
        showLogging: false,
        useBrowserForJS: true,
        borderColor: isPreview ? '#ffffff' : null,
        showGrid: true,
        showAxes: true,
        language: 'fr',
        appletOnLoad: (api) => {
          ggbApiRef.current = api;
          setGgbReady(true);
          setLoading(false);

          if (initialConfigRef.current) {
            api.setBase64(initialConfigRef.current);

            // En preview : fixer les objets après chargement (setBase64 est async)
            if (isPreview) {
              setTimeout(() => {
                try {
                  const names = api.getAllObjectNames();
                  for (let i = 0; i < names.length; i++) {
                    api.setFixed(names[i], true);
                  }
                } catch (e) {}
              }, 500);
            }
          }

          // Labels des points + config preview
          try {
            const objNames = api.getAllObjectNames();
            for (let i = 0; i < objNames.length; i++) {
              const name = objNames[i];
              if (api.getObjectType(name) === 'point') {
                api.setLabelVisible(name, true);
              }
            }

            // En preview : masquer l'algèbre + fixer les objets
            if (isPreview) {
              api.setPerspective('G');
              api.setVisible('algebra', false);
              for (let i = 0; i < objNames.length; i++) {
                api.setAlgebraVisible(objNames[i], false);
                api.setFixed(objNames[i], true);
              }
            }
          } catch (e) {}

          // Forcer un redraw après initialisation (corrige l'affichage vide)
          setTimeout(() => {
            try {
              api.evalCommand('UpdateConstruction()');
            } catch (e) {}
          }, 300);

          if (!isPreview) {
            // Quand un objet est ajouté : force le label visible pour les points, désactive la trace
            api.registerAddListener((name) => {
              try {
                if (api.getObjectType(name) === 'point') {
                  api.setLabelVisible(name, true);
                }
                api.setTrace(name, false);
              } catch (e) {}
              debouncedSave();
            });
            api.registerUpdateListener(() => debouncedSave());
            api.registerRemoveListener(() => debouncedSave());
            // Capte les changements UI (grille, axes, style, vue...)
            api.registerClientListener((evt) => {
              if (['updateStyle', 'viewChanged2D', 'perspectiveChanged', 'setMode'].includes(evt.type)) {
                debouncedSave();
              }
            });
          }
        }
      };

      const applet = new window.GGBApplet(ggbParams, true);
      ggbAppletRef.current = applet;

      const originalSetInterval = window.setInterval;
      window.setInterval = function(...args) {
        const id = originalSetInterval.apply(window, args);
        intervalIdsRef.current.push(id);
        return id;
      };

      applet.inject(ggbElementId.current);

      setTimeout(() => {
        window.setInterval = originalSetInterval;
      }, 3000);
    };

    init();
    return cleanupGeoGebra;
  }, [loadGeoGebra, isPreview, debouncedSave, cleanupGeoGebra]);

  // Mode preview
  if (isPreview) {
    if (!config?.ggb_base64) return null;

    return (
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden my-3">
        <style>{`
          #${ggbElementId.current} .ggb_logo,
          #${ggbElementId.current} .GeoGebraLogo,
          #${ggbElementId.current} [class*="logo"],
          #${ggbElementId.current} .examButton {
            display: none !important;
          }
        `}</style>
        <div className="relative" style={{ minHeight: '350px' }}>
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 z-10">
              <div className="w-8 h-8 border-3 border-cyan-500 border-t-transparent rounded-full animate-spin mb-2" />
              <span className="text-sm text-slate-500">Chargement...</span>
            </div>
          )}
          <div id={ggbElementId.current} className="flex justify-center" />
        </div>
      </div>
    );
  }

  // Contenu de l'éditeur (partagé entre mode normal et fullscreen)
  const editorContent = (
    <div className={`border border-sky-200 overflow-hidden bg-white ${
      isFullscreen ? 'w-full max-w-[1500px] h-full max-h-[950px] rounded-xl flex flex-col' : 'rounded-xl'
    }`}>
      {/* Actions + Templates */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-sky-100 flex-wrap">
        {/* Templates */}
        <div className="flex flex-wrap gap-1 flex-1">
          {quickTemplates.map(tpl => (
            <button
              key={tpl.id}
              onClick={() => applyTemplate(tpl)}
              disabled={!ggbReady}
              title={tpl.label}
              className="flex items-center gap-1 px-2 py-1 bg-gray-50 border border-gray-200 rounded-md text-[10px] text-gray-600
                         hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <span className="text-sm">{tpl.icon}</span>
              <span>{tpl.label}</span>
            </button>
          ))}
        </div>

        {/* Boutons actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={resetCanvas}
            className="p-1.5 rounded-md bg-gray-50 border border-gray-200 text-gray-500 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200 transition"
            title="Effacer tout"
          >
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={toggleFullscreen}
            className="p-1.5 rounded-md bg-gray-50 border border-gray-200 text-gray-500 hover:bg-sky-50 hover:text-sky-600 hover:border-sky-200 transition"
            title={isFullscreen ? 'Quitter le plein écran (Échap)' : 'Plein écran'}
          >
            {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {/* Zone GeoGebra */}
      <div className={isFullscreen ? 'flex-1 flex items-center justify-center p-2' : ''}>
        <div
          ref={ggbZoneRef}
          className={`relative bg-white overflow-hidden ${isFullscreen ? 'w-full h-full rounded-lg' : 'w-fit mx-auto'}`}
          style={{ minHeight: isFullscreen ? undefined : '400px' }}
        >
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50 z-10">
              <div className="w-7 h-7 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mb-2" />
              <span className="text-xs text-gray-400">Chargement de GeoGebra...</span>
            </div>
          )}
          <div id={ggbElementId.current} className="flex justify-center" />
        </div>
      </div>
    </div>
  );

  // Mode édition — structure DOM stable pour préserver l'iframe GeoGebra
  return (
    <div className={isFullscreen
      ? 'fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-6'
      : ''
    }>
      {editorContent}
    </div>
  );
};

export default GeogebraElement;
